import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sparkle, UploadSimple, Spinner, X as XIcon, Envelope, UsersThree } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClassSummaryDialog({ classId, classTitle, level, onClose }) {
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/groups`, { withCredentials: true });
        const list = r.data || [];
        setGroups(list);
        // Pre-select all groups matching the class level by default — the
        // teacher can deselect any of them before sending.
        const matching = list.filter(g => !level || g.level === level).map(g => g.group_id);
        setSelectedGroups(matching.length ? matching : list.map(g => g.group_id));
      } catch { /* groups optional */ }
    })();
  }, [level]);

  const toggleGroup = (gid) => {
    setSelectedGroups(prev => prev.includes(gid) ? prev.filter(g => g !== gid) : [...prev, gid]);
  };

  const submit = async () => {
    if (!notes.trim() && !file) {
      toast.error('Schreibe Notizen oder lade eine Aufnahme hoch.');
      return;
    }
    if (selectedGroups.length === 0) {
      toast.error('Bitte mindestens eine Klasse auswählen.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (notes.trim()) fd.append('notes', notes.trim());
      if (file) fd.append('audio', file);
      fd.append('group_ids', selectedGroups.join(','));
      const res = await axios.post(`${API}/classes/${classId}/summary`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setResult(res.data);
      toast.success(`Zusammenfassung erstellt — ${res.data.emails_sent || 0} E-Mails versandt`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erstellung fehlgeschlagen';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="class-summary-dialog">
      <div className="bg-white rounded-2xl border border-stone-200 shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-stone-200">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkle size={20} weight="duotone" className="text-[#AB1C24]" />
            <h2 className="font-heading font-semibold text-[#292524] truncate">
              Zusammenfassung erstellen · {classTitle}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-[#A8A29E] hover:text-[#292524] hover:bg-stone-100 rounded-md"
            data-testid="class-summary-close"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {!result ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-1.5">
                  Aufnahme (optional)
                </label>
                <p className="text-xs text-[#78716C] mb-2">
                  MP3, M4A, WAV oder WebM bis 25 MB. Wird mit Whisper transkribiert.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <label
                    htmlFor="cs-audio"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-300 bg-white hover:border-[#AB1C24] text-sm font-medium cursor-pointer"
                    data-testid="class-summary-audio-label"
                  >
                    <UploadSimple size={16} />
                    {file ? 'Datei ändern' : 'Audio auswählen'}
                  </label>
                  <input
                    id="cs-audio"
                    ref={fileRef}
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                  {file && (
                    <span className="text-xs text-[#57534E] truncate max-w-[260px]">
                      {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#292524] mb-1.5">
                  Notizen / Gesehene Themen
                </label>
                <p className="text-xs text-[#78716C] mb-2">
                  Optional. Falls keine Aufnahme vorhanden, hier kurze Stichpunkte zur Stunde.
                </p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  placeholder="Heute haben wir … Konjugation Presente, Vokabeln zum Thema Familie, Aussprache von 'r' …"
                  className="rounded-xl border-stone-300"
                  data-testid="class-summary-notes"
                />
              </div>

              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-[#57534E]">
                <strong className="text-[#292524]">Was passiert?</strong> Die KI erstellt eine
                pädagogische Zusammenfassung mit Highlights, neuen Vokabeln, Grammatik-Fokus und
                Hausaufgabe. Sie wird automatisch per E-Mail an die Schüler:innen der
                ausgewählten Klassen versendet.
              </div>

              {/* Klassen-Auswahl: send to the chosen class groups instead of by level. */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-1.5 flex items-center gap-1.5">
                  <UsersThree size={14} weight="duotone" /> Empfänger-Klassen
                </label>
                <p className="text-xs text-[#78716C] mb-2">
                  Standardmäßig alle Klassen des Niveaus <strong>{level || 'A2'}</strong>. Du kannst auch andere Klassen hinzufügen oder ausschließen.
                </p>
                {groups.length === 0 ? (
                  <p className="text-xs text-[#A8A29E] italic">Keine Klassen verfügbar.</p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="cs-group-picker">
                    {groups.map(g => {
                      const checked = selectedGroups.includes(g.group_id);
                      return (
                        <button
                          key={g.group_id}
                          type="button"
                          onClick={() => toggleGroup(g.group_id)}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                            checked
                              ? 'bg-[#AB1C24] border-[#AB1C24] text-white'
                              : 'bg-white border-stone-300 text-[#57534E] hover:border-[#AB1C24]'
                          }`}
                          data-testid={`cs-group-${g.group_id}`}
                        >
                          {checked ? '✓ ' : ''}{g.name} · {g.level}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="class-summary-result">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2">
                <Envelope size={16} weight="duotone" />
                Zusammenfassung generiert. <strong>{result.emails_sent || 0}</strong> E-Mails versandt.
              </div>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[#292524] bg-white border border-stone-200 rounded-lg p-4 max-h-[50vh] overflow-y-auto">
                {result.summary_markdown}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-stone-200 flex justify-end gap-2">
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="class-summary-cancel">
                Abbrechen
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || (!notes.trim() && !file)}
                className="bg-[#AB1C24] hover:bg-[#8A151C] text-white"
                data-testid="class-summary-submit"
              >
                {submitting
                  ? <><Spinner size={16} className="animate-spin mr-2" /> Generiere…</>
                  : <><Sparkle size={16} className="mr-2" weight="fill" /> Erstellen &amp; versenden</>}
              </Button>
            </>
          ) : (
            <Button onClick={onClose} className="bg-[#AB1C24] hover:bg-[#8A151C] text-white" data-testid="class-summary-done">
              Schließen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
